/**
 * 不变量框架 — 开发/测试模式下每个 Tick 后检查。
 * 见 docs/06-quality/simulation-invariants.md。生产可关闭高成本全量检查。
 */

import type { WorldState } from './world-state.js';

export interface InvariantViolation {
  readonly invariantId: string;
  readonly message: string;
}

export interface Invariant {
  readonly id: string;
  check(state: WorldState): InvariantViolation | null;
}

function violation(invariantId: string, message: string): InvariantViolation {
  return { invariantId, message };
}

/** 递归检查所有数值有限（非 NaN/Infinity）。 */
function allNumbersFinite(value: unknown, path: string): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? null : path;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const r = allNumbersFinite(value[i], `${path}[${i}]`);
      if (r !== null) return r;
    }
    return null;
  }
  if (typeof value === 'object' && value !== null) {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = allNumbersFinite(v, path ? `${path}.${k}` : k);
      if (r !== null) return r;
    }
  }
  return null;
}

/** 内置通用不变量（内核级；领域不变量由后续 Goal 追加）。 */
export function builtinInvariants(): Invariant[] {
  return [
    {
      id: 'finite_numbers',
      check(state) {
        const bad = allNumbersFinite(state.resources.stock, 'resources.stock');
        return bad === null ? null : violation('finite_numbers', `non-finite number at ${bad}`);
      },
    },
    {
      id: 'clock_monotonic',
      check(state) {
        return Number.isInteger(state.clock.tick) && state.clock.tick >= 0
          ? null
          : violation('clock_monotonic', `tick must be a non-negative integer, got ${state.clock.tick}`);
      },
    },
    {
      id: 'entity_id_unique',
      check(state) {
        const seen = new Set<string>();
        for (const id of state.entities.order) {
          if (seen.has(id)) return violation('entity_id_unique', `duplicate entity id ${id}`);
          seen.add(id);
        }
        return null;
      },
    },
    {
      id: 'entity_components_consistent',
      check(state) {
        for (const id of state.entities.order) {
          if (!(id in state.entities.kind))
            return violation('entity_components_consistent', `entity ${id} missing kind`);
        }
        const orderSet = new Set<string>(state.entities.order);
        for (const id of Object.keys(state.entities.kind)) {
          if (!orderSet.has(id))
            return violation('entity_components_consistent', `kind for unknown entity ${id}`);
        }
        return null;
      },
    },
  ];
}

export class InvariantRegistry {
  private readonly items: Invariant[] = [];
  private enabled = true;

  constructor(invariants: readonly Invariant[] = builtinInvariants()) {
    this.items.push(...invariants);
  }

  register(invariant: Invariant): void {
    this.items.push(invariant);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** 检查全部不变量，返回所有违规（空数组 = 通过）。 */
  checkAll(state: WorldState): readonly InvariantViolation[] {
    if (!this.enabled) return [];
    const out: InvariantViolation[] = [];
    for (const inv of this.items) {
      const v = inv.check(state);
      if (v !== null) out.push(v);
    }
    return out;
  }
}
